import { ref } from "vue";
import { message } from "ant-design-vue";
import { Device } from "@twilio/voice-sdk";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import { shouldOpenPostCallForm, getCampaignCallSid } from "@/src/utils/callsUtils.js";
import store from "@/src/store";
import router from "@/src/router/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import twilioEventHandler from "./twilioEventHandler.js";
import sentryService from "@/src/services/sentry/sentryService.js";
import BaseTelephonyDevice from "@/src/services/telephony/base/baseTelephonyDevice.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { playIncomingRingtone, stopIncomingRingtone } from "@/src/utils/audioNotification.js";

export default class TwilioDevice extends BaseTelephonyDevice {
  constructor() {
    super(telephonyProviders.twilio);
    this.device = null;
    this.rawIncomingConnection = null;
    this.campaignCallSid = null;
    this.isReady = ref(false);
    this.currentConnection = ref(null);
    this.incomingConnection = ref(null);
    this.lastRefreshTime = Date.now();
  }

  async initialize(token) {
    if (!token || typeof token !== "string" || token.trim() === "") {
      throw new Error(
        "Valid token string is required for Twilio device initialization",
      );
    }

    if (this.device) {
      try {
        this.device.destroy();
        await this.delay(100);
      } catch (error) {
        console.error("Error destroying previous device:", error);
      }
    }

    this.device = new Device(token, {
      debug: true,
      answerOnBridge: true,
      codecPreferences: ["opus", "pcmu"],
    });

    this.device.on("registered", () => {
      console.log("Twilio device registered successfully");
      this.isReady.value = true;
      if (this.device.audio) {
        this.device.audio.incoming(false);
      }
    });

    this.device.on("error", (error) => {
      console.error("Twilio device error:", error);
      this.handleDeviceError(error);
    });

    this.device.on("messageReceived", (message) => {
      twilioEventHandler.handleCustomMessage(message);
    });

    this.device.on("tokenWillExpire", async () => {
      try {
        const response = await CallService.getCallToken({
          provider: telephonyProviders.twilio,
        });
        if (response?.success && response.token) {
          this.device.updateToken(response.token);
        }
      } catch (error) {
        console.error("Error refreshing token:", error);
      }
    });

    this.device.on("incoming", (connection) => {
      console.log(`[${this.provider}] Incoming call - From: ${connection.parameters.From}`);
      if (this.rawIncomingConnection) {
        stopIncomingRingtone();
        this.rawIncomingConnection.reject();
      }

      this.rawIncomingConnection = connection;
      this.incomingConnection.value = connection;

      const customParams = connection.customParameters;
      const entityId = customParams?.get('EntityId');
      const entityType = customParams?.get('EntityType');
      const ringtoneUrl = customParams?.get('RingtoneUrl') || null;
      const callerName = customParams?.get('CallerName');

      const fromRaw = connection.parameters.From || "";
      const isInternalIdentity =
        fromRaw.startsWith("client:") || /^user:\d+:\d+$/.test(fromRaw);

      let resolvedName = callerName;
      if (!resolvedName) {
        const m = fromRaw.match(/^(?:client:)?user:(\d+):(\d+)$/);
        if (m) {
          const agent = store.getters["users/users"]?.find(
            (u) => String(u.id) === String(m[1]),
          );
          if (agent?.name) resolvedName = agent.name;
        }
      }

      const fallbackName = isInternalIdentity ? "Internal user" : fromRaw;

      const callerDetails = {
        name: resolvedName || fallbackName || "Unknown Caller",
        number: isInternalIdentity ? "Transferred call" : fromRaw,
        From: isInternalIdentity ? "" : fromRaw,
        status: "pending",
        entityId: entityId,
        entityType: entityType,
      };

      playIncomingRingtone(ringtoneUrl);
      store.dispatch("calls/startIncomingCall", callerDetails);

      connection.on("accept", () => {
        stopIncomingRingtone();
        store.commit("calls/UPDATE_CALL_STATUS", { status: "open" });
        store.dispatch(
          "conferenceCall/setMyCallSid",
          connection.parameters.CallSid,
        );
      });

      connection.on("disconnect", () => {
        stopIncomingRingtone();
        store.commit("calls/UPDATE_CALL_STATUS", { status: "closed" });
        store.dispatch("calls/endIncomingCall");

        setTimeout(() => {
          eventBus.emit("call-log-added", true);
        }, 1200);
        this.incomingConnection.value = null;
        this.rawIncomingConnection = null;
      });

      connection.on("reject", () => {
        stopIncomingRingtone();
        store.dispatch("calls/endIncomingCall");
        this.incomingConnection.value = null;
        this.rawIncomingConnection = null;
      });

      connection.on("messageReceived", (message) => {
        twilioEventHandler.handleCustomMessage(message);
      });

      connection.on("cancel", () => {
        stopIncomingRingtone();
        store.dispatch("calls/endIncomingCall");
        this.incomingConnection.value = null;
        this.rawIncomingConnection = null;
      });
    });

    twilioEventHandler.initialize(this.device);
    await this.device.register();
  }

  async makeCall(params) {
    this._validateFeature("callOutbound");

    console.log(`[${this.provider}] Outgoing call - From: ${params.From}, To: ${params.To}`);

    const deviceReady = await this.ensureDeviceInitialized();
    if (!deviceReady) {
      throw new Error("Device could not be initialized");
    }

    await this.disconnectAllCalls();
    await this.delay(100);

    const customPayload = JSON.stringify({
      AssociatedEntityID: params.Id,
      Direction: "outbound",
      CampaignId: params.CampaignId,
      EntityType: params.entityType,
      TaskId: params.taskId,
      CallerId: params.callerId,
    });

    try {
      this.currentConnection.value = await this.device.connect({
        params: {
          To: params.To,
          From: params.From,
          CustomPayload: customPayload,
        },
      });

      this.setupConnectionEventHandlers(this.currentConnection.value);
      store.dispatch(
        "conferenceCall/setMyCallSid",
        this.currentConnection.value?.parameters?.CallSid,
      );

      return this.currentConnection.value;
    } catch (error) {
      console.error("Error making call:", error);
      if (error.message?.includes("A Call is already active")) {
        if (this.currentConnection.value) {
          await this.hangupCall();
          await this.delay(500);
          try {
            this.currentConnection.value = await this.device.connect({
              params: {
                To: params.To,
                From: params.From,
                CustomPayload: customPayload,
              },
            });
            this.setupConnectionEventHandlers(this.currentConnection.value);
            return this.currentConnection.value;
          } catch (retryError) {
            console.error("Retry failed:", retryError);
            this.handleCallError(retryError);
            throw retryError;
          }
        }
      }
      this.handleCallError(error);
      throw error;
    }
  }

  setupConnectionEventHandlers(connection) {
    connection.on("messageReceived", async (message) => {
      if (!message?.content) return;
      await twilioEventHandler.handleCustomMessage(message);
      const { campaign_pickup } = message.content;
      if (campaign_pickup) {
        try {
          const { callSid, entity_id, entity_type } = campaign_pickup;
          this.campaignCallSid = callSid;
          store.dispatch("callsCampaign/setCampaignCallSid", callSid);
        } catch (error) {
          console.error("Failed to update call status:", error);
        }
      }
    });

    connection.on("error", (error) => {
      console.error("Call connection error:", error);
      this.handleCallError(error);
    });

    connection.on("disconnect", () => {
      console.log("Call disconnected");
      this.handleCallEnd();
    });

    connection.on("cancel", () => {
      console.log("Call cancelled");
      this.handleCallEnd();
    });

    connection.on("reject", () => {
      console.log("Call rejected");
      this.handleCallEnd();
    });
  }

  handleCallError(error) {
    const errorCode = error.code;
    const errorMessage = error.message;

    console.error(`Call error [${errorCode}]: ${errorMessage}`);

    switch (errorCode) {
      case 31000:
        message.error("Call failed: Invalid phone number format.").then();
        break;
      case 31005:
        console.error("Error sent from gateway in HANGUP");
        break;
      case 20003:
        message
          .error("Call failed: Authentication failed. Please refresh the page.")
          .then();
        break;
      case 53000:
        message
          .error(
            "Call failed: Unable to create call. Please check your account balance.",
          )
          .then();
        break;
      case 53400:
        message
          .error("Call failed: Forbidden number. This number cannot be called.")
          .then();
        break;
      case 31401:
        message
          .error(
            "Call failed: We’re unable to access your microphone. Please enable mic permissions and try again.",
          )
          .then();
        break;
      case 31402:
        message
          .error(
            "Call failed: Microphone not detected. Please enable mic access and try again.",
          )
          .then();
        break;
      default:
        sentryService.captureError(error, {
          tags: {
            provider: "twilio",
            errorType: "call_error",
            errorCode: errorCode || "No code",
            feature: "telephony",
          },
          extra: {
            errorMessage,
            deviceStatus: this.getDeviceStatus(),
            campaignCallSid: this.campaignCallSid,
            timestamp: new Date().toISOString(),
            userID: localStorage.getItem("userID") || "Not Available",
            workspaceID:
              WorkspaceService.getCurrentWorkspaceId() || "Not Available",
          },
        });

        message.info("Connecting....").then();
        break;
    }

    this.handleCallEnd();
  }

  handleDeviceError(error) {
    console.error("Device error:", error);
    this.updateCallState();
  }

  async ensureDeviceInitialized(retryCount = 0) {
    if (!this.device && retryCount < 3) {
      try {
        const response = await CallService.getCallToken({
          provider: telephonyProviders.twilio,
        });
        if (response?.success && response.token) {
          await this.initialize(response.token);
        }
      } catch (error) {
        console.error("Failed to get token during retry:", error);
      }

      if (!this.device) {
        await this.delay(2000);
        return this.ensureDeviceInitialized(retryCount + 1);
      }
    }

    if (this.device && this.device.state === "destroyed") {
      this.device = null;
      this.isReady.value = false;
      if (retryCount < 3) {
        return this.ensureDeviceInitialized(retryCount + 1);
      }
    }

    return !!this.device;
  }

  async answerIncomingCall() {
    console.log(`[${this.provider}] Answering incoming call`);
    stopIncomingRingtone();

    if (this.rawIncomingConnection) {
      try {
        if (this.currentConnection.value) {
          await this.hangupCall();
          await this.delay(100);
        }

        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (permissionError) {
          console.error("Audio permission denied:", permissionError);
          if (
            permissionError.name === "NotFoundError" ||
            permissionError.name === "NotReadableError"
          ) {
            message
              .error(
                "Microphone not available. Please check your audio device settings.",
              )
              .then();
          }
          this.rawIncomingConnection.reject();
          await store.dispatch("calls/endIncomingCall");
          this.incomingConnection.value = null;
          this.rawIncomingConnection = null;
          eventBus.emit("audio-permission-denied");
          return null;
        }

        this.currentConnection.value =
          await this.rawIncomingConnection.accept();
        store.commit("calls/UPDATE_CALL_STATUS", { status: "open" });

        this.currentConnection.value?.on("messageReceived", (message) => {
          twilioEventHandler.handleCustomMessage(message);
        });

        this.currentConnection.value?.on("disconnect", () => {
          this.currentConnection.value = null;
          this.updateCallState();
        });

        return this.rawIncomingConnection;
      } catch (error) {
        console.error("Error answering call:", error);
        await store.dispatch("calls/endIncomingCall");
        this.incomingConnection.value = null;
        this.rawIncomingConnection = null;
        return null;
      }
    }
    return null;
  }

  async rejectIncomingCall() {
    try {
      stopIncomingRingtone();
      if (this.rawIncomingConnection) {
        this.rawIncomingConnection.reject();
        store.dispatch("calls/endIncomingCall").then();
        this.incomingConnection.value = null;
        this.rawIncomingConnection = null;
      }
    } catch (error) {
      console.error("An error occurred while rejecting incoming call: ", error);
    }
  }

  async hangupCall() {
    try {
      if (this.currentConnection.value) {
        const connection = this.currentConnection.value;
        connection.disconnect();
        await this.delay(100);
        this.updateCallState();
      }
    } catch (error) {
      console.error("An error occurred while hanging up the call: ", error);
      this.currentConnection.value = null;
      this.updateCallState();
    }
  }

  async hangupIncomingCall() {
    stopIncomingRingtone();
    if (this.rawIncomingConnection) {
      const callSid = this.rawIncomingConnection?.parameters?.CallSid;
      const connection = this.rawIncomingConnection;
      this.rawIncomingConnection = null;
      this.incomingConnection.value = null;
      connection.disconnect();
      await this.delay(100);

      if (this.currentConnection.value) {
        this.handleCallEnd();
      } else {
        if (callSid) {
          eventBus.emit("call-ended", callSid);
        }
        this.updateCallState();
      }
    }
  }

  async toColdTransfer(params) {
    this._validateFeature("coldTransfer");

    const deviceReady = await this.ensureDeviceInitialized();
    if (!deviceReady) {
      throw new Error("Device could not be initialized");
    }

    const payload = {
      to:
        this.currentConnection.value?.customParameters?.get("To") ||
        this.incomingConnection.value?.parameters?.From,
      from:
        this.currentConnection.value?.customParameters?.get("From") ||
        this.incomingConnection.value?.parameters?.To,
      type: params?.type,
      type_id: params?.typeId,
      call_sid:
        this.currentConnection.value?.parameters?.CallSid ||
        this.incomingConnection.value?.parameters?.CallSid,
      transfer_number: params?.transferNumber,
    };

    const response = await CallService.transferCall(payload);
    if (response?.success) {
      message.success("Call Transferred successfully!!");
    }
  }

  async toWarmTransfer(params) {
    this._validateFeature("warmTransfer");

    const deviceReady = await this.ensureDeviceInitialized();
    if (!deviceReady) {
      throw new Error("Device not available");
    }

    await store.dispatch("conferenceCall/setTransferringCall", true);

    const payload = {
      to:
        this.currentConnection.value?.customParameters?.get("To") ||
        this.incomingConnection.value?.parameters?.From,
      from:
        this.currentConnection.value?.customParameters?.get("From") ||
        this.incomingConnection.value?.parameters?.To,
      type: params?.type,
      type_id: params?.typeId,
      call_sid:
        this.currentConnection.value?.parameters?.CallSid ||
        this.incomingConnection.value?.parameters?.CallSid,
      transfer_number: params?.transferNumber,
    };

    const response = await CallService.warmTransfer(payload);
    if (!response?.success) {
      await store.dispatch("conferenceCall/setTransferringCall", false);
      return response;
    }

    const data = {
      ...response?.data,
      transfer_name: params?.transferName,
      transfer_phone_number: params?.transferNumber,
    };
    await store.dispatch("conferenceCall/startWarmTransfer");
    await store.dispatch("conferenceCall/setConferenceData", data);
    return response;
  }

  async swapCalls() {
    this._validateFeature("callSwap");

    const id = store.getters["conferenceCall/conferenceSid"];
    const payload = {
      conference_id: id,
    };

    const response = await CallService.swapCallsInConference(payload);
    if (response?.success) {
      await store.dispatch(
        "conferenceCall/setParticipants",
        response?.participants,
      );
    }
    return response;
  }

  async mergeConference() {
    // Piggybacks on warmTransfer capability rather than introducing a new
    // feature key — merge is only reachable from inside the warm-transfer flow.
    this._validateFeature("warmTransfer");

    const id = store.getters["conferenceCall/conferenceSid"];
    if (!id) {
      throw new Error("No active conference to merge");
    }

    await store.dispatch("conferenceCall/setMerging", true);

    try {
      const response = await CallService.mergeConference({ conference_id: id });
      if (response?.success) {
        await store.dispatch("conferenceCall/setMerged", true);
        if (Array.isArray(response?.participants)) {
          await store.dispatch(
            "conferenceCall/setParticipants",
            response.participants,
          );
        }
      }
      return response;
    } finally {
      await store.dispatch("conferenceCall/setMerging", false);
    }
  }

  async startRecording() {
    this._validateFeature("callRecording");

    const callSid =
      this.currentConnection.value?.parameters?.CallSid ||
      this.incomingConnection.value?.parameters?.CallSid;

    if (callSid) {
      return await CallService.initiateCallRecording(callSid);
    }
  }

  async stopRecording() {
    this._validateFeature("callRecording");

    const callSid =
      this.currentConnection.value?.parameters?.CallSid ||
      this.incomingConnection.value?.parameters?.CallSid;

    if (callSid) {
      return await CallService.stopCallRecording(callSid);
    }
  }

  async getVoicemails() {
    this._validateFeature("voicemails");
    return await CallService.fetchVoicemails();
  }

  async purchasePhoneNumber(params) {
    this._validateFeature("purchasePhoneNumber");
    return await CallService.purchasePhoneNumber(params);
  }

  async deletePhoneNumber(params) {
    this._validateFeature("deletePhoneNumber");
    return await CallService.deletePhoneNumber(params);
  }

  getDeviceStatus() {
    return {
      isReady:
        this.isReady.value && this.device && this.device.state !== "destroyed",
      hasActiveCall: !!this.currentConnection.value,
      hasIncomingCall: !!this.incomingConnection.value,
    };
  }

  getCurrentConnection() {
    return this.currentConnection.value;
  }

  getIncomingConnection() {
    return this.incomingConnection.value;
  }

  updateCallState() {
    const hasActiveCall = !!this.currentConnection.value;
    if (!hasActiveCall) {
      store.dispatch("calls/endOutgoingCall").then();
      store.dispatch("calls/endIncomingCall").then();
      store.dispatch("conferenceCall/endConference").then();
      setTimeout(() => {
        eventBus.emit("call-hangup", true);
      }, 600);
    }
  }

  async disconnectAllCalls() {
    const promises = [];
    if (this.currentConnection.value) {
      promises.push(this.hangupCall());
    }
    if (this.rawIncomingConnection) {
      promises.push(this.hangupIncomingCall());
    }
    await Promise.all(promises);
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async refresh() {
    console.log("Refreshing Twilio device...");
    try {
      const response = await CallService.getCallToken({
        provider: telephonyProviders.twilio,
      });
      if (response?.success && response.token) {
        await this.initialize(response.token);
        this.lastRefreshTime = Date.now();
        console.log("Twilio device refreshed successfully");
      }
    } catch (error) {
      console.error("Failed to refresh Twilio device:", error);
      throw error;
    }
  }

  destroy() {
    try {
      stopIncomingRingtone();
      if (this.device) {
        this.device.destroy();
      }
      this.isReady.value = false;
      this.currentConnection.value = null;
      this.incomingConnection.value = null;
      this.rawIncomingConnection = null;
    } catch (error) {
      console.error("Error destroying Twilio device:", error);
    }
  }

  handleCallEnd() {
    const callSid = this.currentConnection.value?.parameters?.CallSid;
    const hadActiveConnection = !!this.currentConnection.value;

    if (callSid) {
      const isCampaign = store.getters["calls/isCampaign"];

      if (isCampaign) {
        const campaignCallSid = getCampaignCallSid(callSid, this.campaignCallSid); 
        const campaignId = store.getters["callsCampaign/currentCampaignId"];

        router
          .push({
            name: "sales-dialer-campaign-detail",
            params: { id: campaignId },
          })
          .then();

        if (campaignCallSid) {
          setTimeout(() => {
            eventBus.emit("call-log-added", true);
          }, 1200);
          if (shouldOpenPostCallForm()) {
            store.dispatch("callsCampaign/setDrawerVisible", true).then();
            eventBus.emit("call-ended", campaignCallSid);
          }
        }
        setTimeout(() => {
          store.dispatch("callsCampaign/refreshCampaignDetails").then();
        }, 2000);
        this.campaignCallSid = null;
        store.dispatch("callsCampaign/updateCallOngoing", false).then();
        store.dispatch("callsCampaign/setCampaignCallSid", null);
        eventBus.emit("campaign-next-step", true);
      } else {
          eventBus.emit("call-ended", callSid);
      }
    }

    if (hadActiveConnection) {
      this.currentConnection.value = null;
    }

    this.updateCallState();
  }
}
